/**
 * Attribution tracking: first/last-touch UTM + GCLID capture, appended to
 * Pardot form iframes.
 *
 * Framework-agnostic:
 *  - Gatsby / webpack: import { init, capture } from "./attribution"
 *  - WordPress / plain <script>: exposes window.Attribution
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.Attribution = factory();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var FIRST_TOUCH_KEYS = ["gclid", "gclsrc", "gbraid"];
  var LAST_TOUCH_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
  var ALL_KEYS = FIRST_TOUCH_KEYS.concat(LAST_TOUCH_KEYS);
  var SESSION_ENTRY_KEY = "session_entry";
  var TS_KEY = "attribution_ts";
  var STORED_KEYS = ALL_KEYS.concat([SESSION_ENTRY_KEY]);
  var EXPIRY_MS = 90 * 24 * 60 * 60 * 1000; // 90 days, matches Pardot's cookie lifetime

  // Native src plus the attributes common lazy-loaders (WP Rocket, LiteSpeed, etc.) use.
  var SRC_ATTRS = ["src", "data-src", "data-lazy-src"];
  var DONE_ATTR = "data-attribution";
  var DEFAULT_HOSTS = ["https://info.grmdocumentmanagement.com"];

  // localStorage throws in some private modes or when site data is blocked.
  function get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  function del(k) { try { window.localStorage.removeItem(k); } catch (e) {} }

  function isExpired() {
    var ts = parseInt(get(TS_KEY), 10);
    return !ts || Date.now() - ts > EXPIRY_MS;
  }

  /**
   * Read attribution params from a location (defaults to the current page)
   * into storage. Safe to call on every route change.
   */
  function capture(loc) {
    loc = loc || window.location;
    var params = new URLSearchParams(loc.search);
    var anyPresent = ALL_KEYS.some(function (k) { return params.has(k); });
    var touched = false;

    // A lapsed window starts clean, so stale values never mix into a new one.
    if (isExpired()) STORED_KEYS.concat([TS_KEY]).forEach(del);

    if (anyPresent) {
      // Most-recent touch: overwrite when present, never delete on absence.
      LAST_TOUCH_KEYS.forEach(function (k) {
        var v = params.get(k);
        if (v) set(k, v);
      });
      // First touch: only set if nothing is stored for this window yet.
      FIRST_TOUCH_KEYS.forEach(function (k) {
        var v = params.get(k);
        if (v && !get(k)) set(k, v);
      });
      // A new touch resets the landing path to this page.
      set(SESSION_ENTRY_KEY, loc.pathname);
      touched = true;
    }

    // Landing path for a window that started without attribution params.
    if (!get(SESSION_ENTRY_KEY)) {
      set(SESSION_ENTRY_KEY, loc.pathname);
      touched = true;
    }

    if (touched) set(TS_KEY, String(Date.now()));
  }

  /** Currently stored params as a plain object. */
  function getParams() {
    var out = {};
    STORED_KEYS.forEach(function (k) {
      var v = get(k);
      if (v) out[k] = v;
    });
    return out;
  }

  /** Return `url` with stored params appended, unless it already carries attribution. */
  function decorateUrl(url) {
    var params = getParams();
    var keys = Object.keys(params);
    if (!keys.length) return url;

    var u;
    try { u = new URL(url, document.baseURI); } catch (e) { return url; }
    if (u.searchParams.has("utm_source") || u.searchParams.has("gclid")) return url;

    keys.forEach(function (k) {
      if (!u.searchParams.has(k)) u.searchParams.set(k, params[k]);
    });
    return u.toString();
  }

  function matchesHost(value, hosts) {
    return !!value && hosts.some(function (h) { return value.indexOf(h) === 0; });
  }

  function decorateIframe(iframe, hosts) {
    if (iframe.hasAttribute(DONE_ATTR)) return;
    var matched = false;
    SRC_ATTRS.forEach(function (attr) {
      var value = iframe.getAttribute(attr);
      if (!matchesHost(value, hosts)) return;
      matched = true;
      var next = decorateUrl(value);
      if (next !== value) iframe.setAttribute(attr, next);
    });
    if (matched) iframe.setAttribute(DONE_ATTR, "1");
  }

  /**
   * Capture params, decorate matching iframes already on the page, and keep
   * watching for ones added later (Elementor popups/tabs, Gatsby route
   * changes, lazy-loaders swapping data-src -> src).
   *
   * options.hosts: array of iframe URL prefixes to decorate.
   * Returns a function that stops watching.
   */
  function init(options) {
    var hosts = (options && options.hosts) || DEFAULT_HOSTS;
    capture();

    var selector = hosts
      .map(function (h) {
        return SRC_ATTRS.map(function (a) { return "iframe[" + a + '^="' + h + '"]'; }).join(",");
      })
      .join(",");

    function scan(node) {
      if (!node || node.nodeType !== 1) return;
      if (node.matches(selector)) decorateIframe(node, hosts);
      node.querySelectorAll(selector).forEach(function (f) { decorateIframe(f, hosts); });
    }

    scan(document.documentElement);

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === "attributes") scan(m.target);
        else m.addedNodes.forEach(scan);
      });
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: SRC_ATTRS,
    });

    return function stop() { observer.disconnect(); };
  }

  return { init: init, capture: capture, getParams: getParams, decorateUrl: decorateUrl };
});
